import {Box, createStyles, Stack} from '@mantine/core';
import React from 'react';

import {ProcedureRunTab} from 'studies/redux/ProcedureRunPlanUIReduxActions';
import getTestProps from 'test/lib/cypress/getTestProps';
import {Tab, TabContainer} from 'ui-library/TabContainer';

import {ProcedureRunOverviewScrollProvider} from './procedureRunOverviewScroll';
import {type StudyExecutionHeaderTabsProps} from './StudyExecutionHeader';

interface ProcedureRunLayoutProps {
  header: React.ReactNode;
  tabProps: StudyExecutionHeaderTabsProps<ProcedureRunTab>;
  execution: React.ReactNode;
}

export default function ProcedureRunLayout(props: ProcedureRunLayoutProps): JSX.Element {
  return (
    <ProcedureRunOverviewScrollProvider>
      <ProcedureRunLayoutInner {...props} />
    </ProcedureRunOverviewScrollProvider>
  );
}

function ProcedureRunLayoutInner({header, tabProps, execution}: ProcedureRunLayoutProps): JSX.Element {
  const {classes, cx} = useStyles();
  const isOverviewTab = tabProps.headerTabContainerProps?.activeTab === ProcedureRunTab.OVERVIEW;

  return (
    <Stack
      spacing={0}
      className={classes.container}
      {...getTestProps({component: 'ProcedureRunLayout', element: 'container'})}
    >
      <Box className={classes.header}>{header}</Box>
      {tabProps.headerTabContainerProps ? (
        <Box className={classes.body} {...getTestProps({component: 'ProcedureRunLayout', element: 'body'})}>
          <TabContainer {...tabProps.headerTabContainerProps}>
            {tabProps.headerTabsProps.map((headerTabProps) => (
              <Tab key={headerTabProps.tabKey} {...headerTabProps}>
                {isOverviewTab ? (
                  <Box className={classes.overviewBodyFill}>{execution}</Box>
                ) : (
                  <Box
                    className={classes.scrollableContent}
                    {...getTestProps({component: 'ProcedureRunLayout', element: 'scrollRegion'})}
                  >
                    {execution}
                  </Box>
                )}
              </Tab>
            ))}
          </TabContainer>
        </Box>
      ) : (
        <Box
          className={cx(classes.scrollableContent, classes.execution)}
          {...getTestProps({component: 'ProcedureRunLayout', element: 'scrollRegion'})}
        >
          {execution}
        </Box>
      )}
    </Stack>
  );
}

const useStyles = createStyles((theme) => ({
  // Trap overflow so the header stays put and inner regions scroll.
  container: {
    height: '100%',
    minHeight: 0,
    overflow: 'hidden',
    width: '100%',
  },
  header: {
    flexShrink: 0,
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  // Single scroll region for pinned tab and plan content, so the header stays put while long content
  // scrolls. Design tabs fill it exactly and scroll their own side panels.
  scrollableContent: {
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
  },
  overviewBodyFill: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    minHeight: 0,
    overflow: 'hidden',
  },
  execution: {
    backgroundColor: theme.colors.gray[0],
  },
}));
